import React, { useState, useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';
import './knowledge.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleRight, faEye, faPenToSquare, faXmark } from '@fortawesome/free-solid-svg-icons';
import { apiService } from '../../services/apiService';
import { toast } from 'react-toastify';
import { faCopy, faFileLines } from '@fortawesome/free-regular-svg-icons';
import { Modal } from 'react-bootstrap';

function Attachments() {
    const location = useLocation();
    const currentPath = location.pathname;
    const navigate = useNavigate();
    const { id } = useParams();
    const [originalName, setOriginalName] = useState("");
    const [files, setFiles] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [newName, setNewName] = useState('');
    const [error, setError] = useState('');
    const [catesNames,setCateNames] = useState('');
    const [loading, setLoading] = useState(true);
    const fileInputRef = useRef(null);
    const [articleDetail, setArticleDetail] = useState([]);
    const author_ids = Number(localStorage.getItem('userid'));
    const [authorDetail,setAuthorDetail] = useState('');
    const [uploadPercentage, setUploadPercentage] = useState(0);
    const isAuthor = (authorDetail && authorDetail.id === author_ids && articleDetail.status === "draft");
    useEffect(() => {
        const fetchPreviewData = async () => {
            try {
                const response = await apiService.getRequest(`knowledge-article/${id}`);
                if (response && response.success) {
                    setCateNames(response.data.subcategory);
                    setAuthorDetail(response.data.author);
                    setArticleDetail(response.data);
                }else{
                    if(response && response.authenticate === false){
                        await apiService.postRequest('/auth/logout');
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.href = '/login';
                    }
                }
            } catch (error) {
                console.error('Error fetching article data:', error);
            }
        };

        fetchPreviewData();
    }, [id]);
    const uploadFiles = async (allFiles) => {
        const formData = new FormData();
        
        // Append existing documents to formData
        documents.forEach(doc => {
            formData.append('existingDocuments[]', doc.id);
        });
        
        // Append newly added files to formData
        allFiles.forEach(file => {
            formData.append('documents[]', file.file);
        });
        try {
            const response = await apiService.postRequest(`/knowledge-articles/${id}/documents`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadPercentage(percentCompleted);
                    }
                },
            });
            setUploadPercentage(0);
            if (response && response.success === true) {
                setFiles([]);
                setDocuments(response.data.documents);
            }else{
                if(response && response.authenticate === false){
                    await apiService.postRequest('/auth/logout');
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            }
        } catch (error) {
            // console.error('Error uploading documents:', error);
        }
    };
    useEffect(() => {
        const fetchDocuments = async () => {
            // setLoading(true);
            try {
                const response = await apiService.getRequest(`/knowledge-articles/${id}/documents`);
                if (response && response.success === true) {
                    setDocuments(response.data.documents);
                }else{
                    if(response && response.authenticate === false){
                        await apiService.postRequest('/auth/logout');
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.href = '/login';
                    }
                }
            } catch (error) {
                console.error('Error fetching documents:', error);
                setError('Failed to fetch documents.');
            }finally{
                setLoading(false);
            }
        };
        fetchDocuments();
    }, [id,files]);
    
    const MAX_FILES = 10; // Maximum number of files that can be uploaded
    const MAX_TOTAL_SIZE_MB = 2500; // Maximum total size for all uploads
    const MAX_FILE_SIZE_MB = 250; // Maximum size for each individual file
    const allowedTypes = [
        'image/jpeg', 'image/png', 'image/gif', // image files
        'application/pdf', // pdf files
        'text/csv', // csv files
        'application/msword', // doc files
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx files
        'application/vnd.ms-excel', // xls files
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx files
        'application/vnd.ms-powerpoint',                                          // .ppt
        'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx

        // video files
        'video/mp4',
        'video/webm',
        'video/ogg',
        // audio files
        'audio/mpeg',      // .mp3
        'audio/wav',       // .wav
        'audio/ogg',       // .ogg
        'audio/webm',      // .webm
        'audio/aac',       // .aac
        'audio/mp4',       // .m4a
        'audio/flac',
        'audio/x-m4a'      // .m4a (alternative MIME type)
    ];

    const handleFileChange = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        // Check if total number of files exceeds limit
        if (documents.length + files.length + selectedFiles.length > MAX_FILES) {
            setError(`You can only upload up to ${MAX_FILES} files.`);
            return;
        }

        // Check each file size and type
        for (const file of selectedFiles) {
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setError(`Each file must be less than ${MAX_FILE_SIZE_MB} MB.`);
                return;
            }
            if (!allowedTypes.includes(file.type)) {
                setError('Only image files (JPEG, PNG, GIF), CSV, PDF, XLS, XLSX, PPT, MP4, WEBM, OGG, Audio and Word files are allowed.');
                return;
            }
        }

        // Check total combined size of all files
        const currentSize = files.reduce((acc, file) => acc + file.file.size, 0);
        const newSize = selectedFiles.reduce((acc, file) => acc + file.size, 0);
        if (currentSize + newSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
            setError(`Total file size cannot exceed ${MAX_TOTAL_SIZE_MB} MB.`);
            return;
        }

        setError('');
        const updatedFiles = selectedFiles.map((file) => ({ file, name: file.name }));
        setFiles((prevFiles) => [...prevFiles, ...updatedFiles]);
        fileInputRef.current.value = null;

        // Call the function to upload files after state update
        await uploadFiles([...files, ...updatedFiles]);
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        const droppedFiles = Array.from(event.dataTransfer.files);
        // Check if total number of files exceeds limit
        if (documents.length + files.length + droppedFiles.length > MAX_FILES) {
            setError(`You can only upload up to ${MAX_FILES} files.`);
            return;
        }

        // Check each file size and type
        for (const file of droppedFiles) {
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                setError(`Each file must be less than ${MAX_FILE_SIZE_MB} MB.`);
                return;
            }
            if (!allowedTypes.includes(file.type)) {
                setError('Only image files (JPEG, PNG, GIF), CSV, PDF, XLS, XLSX, PPT, MP4, WEBM, OGG, Audio and Word files are allowed.');
                return;
            }
        }

        // Check total combined size of all files
        const currentSize = files.reduce((acc, file) => acc + file.file.size, 0);
        const newSize = droppedFiles.reduce((acc, file) => acc + file.size, 0);
        if (currentSize + newSize > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
            setError(`Total file size cannot exceed ${MAX_TOTAL_SIZE_MB} MB.`);
            return;
        }

        setError('');
        const updatedFiles = droppedFiles.map((file) => ({ file, name: file.name }));
        setFiles((prevFiles) => [...prevFiles, ...updatedFiles]);

        // Call the function to upload files after state update
        await uploadFiles([...files, ...updatedFiles]);
    };

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(null);
    const [submitloader, setSubmitloader] = useState(false);
    const handleClose = () => {setIsModalOpen(false);setSelectedIndex(null);};
    const handleConfirmDelete = (index) => {
        setSelectedIndex(index);
        setIsModalOpen(true);
    };

    const handleRemoveFile = async (index) => {
        if (selectedIndex === null) return;
        const documentId = documents[selectedIndex].id;
        setSubmitloader(true);
        try {
            const response = await apiService.deleteRequest(`/knowledge-articles/documents/${documentId}`);
            if(response && response.success === true){
                setDocuments((prevDocs) => prevDocs.filter((_, i) => i !== index));
                setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
                toast(response.message || "Document Deleted Successfully")
            }else{
                if(response && response.authenticate === false){
                    await apiService.postRequest('/auth/logout');
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/login';
                }
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            setError('Failed to delete document.');
        }
        finally{
            setSubmitloader(false);
            setIsModalOpen(false);
            setSelectedIndex(null);
        }
    };

    const handleRenameFile = async (index) => {
        if (typeof index === "number" && index >= 0 && index < documents.length) {
            const updatedDocuments = documents.map((doc, i) =>
                i === index ? { ...doc, document_name: newName } : doc
            );
            setDocuments(updatedDocuments);
            setEditIndex(null);
            setNewName("");
            setLoading(true);
            try {
                // Call the API to update the document name in the backend
                const documentId = documents[index].id;
               const response = await apiService.postRequest(`/knowledge-articles/documents/${documentId}`, {
                    document_name: newName,
                });

                if(response && response.success === true){
                    toast(response.message || "Document Renamed Successfully")
                }else{
                    if(response && response.authenticate === false){
                        await apiService.postRequest('/auth/logout');
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.href = '/login';
                    }
                }
            } catch (error) {
                console.error('Error renaming document:', error);
            }finally{
                setLoading(false);
            }
        } else {
            setNewName(originalName);
            setEditIndex(null);
        }
    };
    
    
    const handleDragOver = (event) => {
        event.preventDefault();
    };

    return (
        <div className='container-fluid px-0'>
            <div className='content_container'>
                <Grid size={{ sm: 12 }} container className="main_jobseeker">
                    <Grid size={{sm:12}} container alignItems="center" className="mario_fixers_article">
                        {articleDetail && articleDetail.status === "under_approval" ? (
                            <Grid size={{md:12,xs:12}} container flexDirection="column" className="jobseekerhead">
                                <h3>Under Approval</h3>
                                <span>{catesNames.knowledge_base} <FontAwesomeIcon className='angleright' icon={faAngleRight} /> {catesNames.category_name} <FontAwesomeIcon className='angleright' icon={faAngleRight} /> {catesNames.name}</span>
                            </Grid>
                        ):""}
                        {articleDetail && articleDetail.status === "draft" ? (
                            <Grid size={{md:12,xs:12}} container flexDirection="column" className="jobseekerhead">
                                <h3>Draft</h3>
                                <span>{catesNames.knowledge_base} <FontAwesomeIcon className='angleright' icon={faAngleRight} /> {catesNames.category_name} <FontAwesomeIcon className='angleright' icon={faAngleRight} /> {catesNames.name}</span>
                            </Grid>
                        ):""}
                        {articleDetail && articleDetail.status === "published" ? (
                            <Grid size={{md:12,xs:12}} container flexDirection="column" className="jobseekerhead">
                                <h3>Published</h3>
                                <span>{catesNames.knowledge_base} <FontAwesomeIcon className='angleright' icon={faAngleRight} /> {catesNames.category_name} <FontAwesomeIcon className='angleright' icon={faAngleRight} /> {catesNames.name}</span>
                            </Grid>
                        ):""}
                        {articleDetail && articleDetail.status === "retired" ? (
                            <Grid size={{md:12,xs:12}} container flexDirection="column" className="jobseekerhead">
                                <h3>Retired</h3>
                                <span>{catesNames.knowledge_base} <FontAwesomeIcon className='angleright' icon={faAngleRight} /> {catesNames.category_name} <FontAwesomeIcon className='angleright' icon={faAngleRight} /> {catesNames.name}</span>
                            </Grid>
                        ):""}
                    </Grid>
                    {loading ? (
                        <div className='d-flex justify-content-center w-100'>
                            <img style={{ width: "100px" }} src={require('../../assets/pinkloader.gif')} alt='Loading...' />
                        </div>
                    ) : (<>
                    <Grid size={{ xs: 12 }} container justifyContent="flex-start" className="statusselectbtn mt-4">
                        <div className='knowledgetab_btnredirect'>
                            <button className={currentPath.startsWith("/knowledge/edit-article") ? "active_tabbtn" : ""} onClick={() => navigate(`/knowledge/edit-article/${id}`)}>Article</button>
                            {articleDetail && articleDetail.status === "draft" ? <button onClick={() =>{navigate(`/knowledge/assignments/${id}`)}}>Assignment</button>:
                            <button onClick={() => navigate(`/knowledge/under-approval/show/${id}`)}>Approvals</button>}
                            <button onClick={() => navigate(`/knowledge/history/${id}`)}>History</button>
                            <button className={currentPath.startsWith('/knowledge/attachments') ? "active_tabbtn" : ""} onClick={() => navigate(`/knowledge/attachments/${id}`)}>Attachments</button>
                            <button onClick={() =>{navigate(`/knowledge/discussions/${id}`)}}>Discussions</button>
                            <button onClick={() =>{navigate(`/knowledge/versions/${id}`)}}>Versions</button>
                        </div>
                    </Grid>
                    <Grid size={{ xs: 12 }} container justifyContent="center" className="mt-4">
                        {isAuthor && 
                            <Grid size={{ md:8,xs: 12 }} container justifyContent="space-between" className="mt-4">
                                <div
                                    onDrop={documents.length >= MAX_FILES || !isAuthor ? undefined : handleDrop}
                                    onDragOver={(e) => {
                                        if (documents.length >= MAX_FILES || !isAuthor) {
                                            e.preventDefault();
                                            return;
                                        }
                                        handleDragOver(e);
                                    }}
                                    style={documents.length >= MAX_FILES || !isAuthor ?{backgroundColor:"#cccccc",cursor:"not-allowed"} :{}}
                                    className={`drag_uploaders align-items-center flex-column d-flex ${
                                        documents.length >= MAX_FILES || !isAuthor ? 'disabled' : ''
                                    }`}
                                >
                                    <FontAwesomeIcon className='file_uploadericon' icon={faFileLines} />
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                        id="file-upload"
                                        ref={fileInputRef}
                                        disabled={documents.length >= MAX_FILES || !isAuthor}
                                    />
                                    <label htmlFor="file-upload" style={documents.length >= MAX_FILES || !isAuthor ?{cursor:"not-allowed"} :{}}>Browse Files</label>
                                    <p className='w-100 mb-0 mt-3'>or drop file here</p>
                                </div>
                                {documents.length >= MAX_FILES && <div style={{ color: 'red', margin: '10px 0px' }}>Maximum number of files uploaded ( 10 / 10 )</div>}
                                {error && <div style={{ color: 'red', margin: '10px 0px' }}>{error}</div>}
                                {uploadPercentage > 0 && (
                                    <div style={{ marginTop: "10px", width: "100%", backgroundColor: "#ddd",overflow:"hidden",borderRadius:"20px" }}>
                                        <div
                                            style={{
                                            width: `${uploadPercentage}%`,
                                            backgroundColor: "#C42B7D",
                                            color: "white",
                                            textAlign: "center",
                                            padding: "3px",
                                            }}
                                        >
                                            {uploadPercentage}%
                                        </div>
                                    </div>
                                )}
                            </Grid>
                        }
                        <Grid size={{ xs: 12 }} container justifyContent="space-between" className="mt-4">
                            <Grid size={{ xs: 12 }} container className="mt-4 attach_headerspan">
                                <Grid size={{ sm: 2, xs: 3 }} container>
                                    <span>#</span>
                                </Grid>
                                <Grid size={{ sm: 8, xs: 6 }} container>
                                    <span>File Name</span>
                                </Grid>
                                {isAuthor && 
                                    <Grid size={{ sm: 2, xs: 3 }} container>
                                        <span>Actions</span>
                                    </Grid>
                                }
                            </Grid>
                            
                            {Array.isArray(documents) && documents.length > 0 ? (
                                documents.map((doc, index) => (
                                <Grid key={doc.id} size={{ xs: 12 }} container justifyContent="flex-start" alignItems="center" className="tablelisterattach">
                                    <Grid size={{ sm: 2, xs: 3 }} container>
                                        <span>{index + 1}</span>
                                    </Grid>
                                    <Grid size={{ sm: 8, xs: 6 }} container>
                                        {/* <a href={doc.file_path} target="_blank" rel="noopener noreferrer">View Document</a> */}
                                        { editIndex === index ? (
                                        <Grid container size={{xs:12}} className="">
                                            <input
                                            className='rename_inputer'
                                                type="text"
                                                value={newName}
                                                onChange={(e) => {
                                                    setNewName(e.target.value);
                                                    if (files[index]) {
                                                        setOriginalName(files[index].name);
                                                    }
                                                }}
                                                placeholder="New document name"
                                            />
                                            <button className='save_ataach registersendbtn1' onClick={() => handleRenameFile(index)}>Save</button>
                                            <button className='save_ataach registersendbtn1' onClick={() => handleRenameFile("")}>Cancel</button>
                                        </Grid>
                                    ):<span className='filenamesshow'>{doc.document_name || 'Untitled'}</span>}
                                    </Grid>
                                    {isAuthor && isAuthor ? 
                                        <Grid size={{ sm: 2, xs: 3 }} container alignItems="center">
                                            <button onClick={() => handleConfirmDelete(index)} disabled={!isAuthor}>
                                                <FontAwesomeIcon icon={faXmark} />
                                            </button>
                                            <button onClick={() => {
                                                setEditIndex(index);
                                                setNewName(doc.document_name || '');
                                            }} disabled={!isAuthor}>
                                                <FontAwesomeIcon icon={faPenToSquare} />
                                            </button>
                                            <a href={`${doc.file_path}`} target="_blank" rel="noopener noreferrer">
                                                <FontAwesomeIcon icon={faEye} />
                                            </a>
                                            {doc && ["mp4", "webm", "ogg"].includes(doc.extension?.toLowerCase()) &&
                                            <button onClick={() => {navigator.clipboard.writeText(`${doc.file_path}`); toast('Link copied to clipboard'); }}>
                                                <FontAwesomeIcon icon={faCopy} />
                                            </button>
                                            }
                                        </Grid> :
                                        <Grid size={{ sm: 2, xs: 3 }} container alignItems="center">
                                        <a href={`${doc.file_path}`} target="_blank" rel="noopener noreferrer">
                                            <FontAwesomeIcon icon={faEye} />
                                        </a>
                                        {doc && ["mp4", "webm", "ogg"].includes(doc.extension?.toLowerCase()) &&
                                            <button onClick={() => {navigator.clipboard.writeText(`${doc.file_path}`); toast('Link copied to clipboard'); }}>
                                                <FontAwesomeIcon icon={faCopy} />
                                            </button>
                                        }
                                    </Grid>
                                    }
                                </Grid>
                                ))
                            ) : (
                                <Grid size={{xs:12}} container justifyContent="center" alignItems="center" className="mt-3">
                                    <span>No data</span>
                                </Grid>
                            )}
                            
                        </Grid>
                    </Grid>
                    </>)}
                    
                </Grid>
                <Modal centered show={isModalOpen} onHide={handleClose}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Delete</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>Are you sure you want to delete this Attachment?</Modal.Body>
                    <Modal.Footer>
                        <button className='modal_cancel' onClick={handleClose}>
                            Cancel
                        </button>
                        <button className='modal_delete' onClick={handleRemoveFile} disabled={loading}>
                            {submitloader === true ? (
                                <img src={require('../../assets/pinkloader.gif')} alt='' />
                            ) : (
                                'Delete'
                            )}
                        </button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
}

export default Attachments;
